/**
 * Browser-router glue for the Joomla-hosted member SPA. Joomla owns the SEF
 * URLs: the host view emits, per member tab, the server-generated `url` plus a
 * canonical client `route`. BrowserRouter runs under a basename of
 * `Uri::root(true)` (the subfolder), and these helpers translate between the
 * two so deep links, refreshes and right-click / open-in-new-tab keep working.
 *
 * Mirrors the CommunityQuiz site routing model, flattened for the member tabs
 * (no nested entity ids).
 */
import { joomlaOptions } from '@services/joomla';
import type { MemberScreenId } from './navigation';

/** History-state key carrying the canonical route across a client-side nav. */
export const ROUTE_STATE_KEY = 'rwInternalRoute';

interface MemberRoute {
  id: MemberScreenId;
  /** Canonical client path (what the React router resolves on). */
  path: string;
  /** Joomla site view name the SEF URL is built from. */
  view: string;
}

/** The single source of truth mapping member tabs ↔ client paths ↔ Joomla views. */
export const MEMBER_ROUTES: MemberRoute[] = [
  { id: 'u-dashboard', path: '/', view: 'app' },
  { id: 'u-activity', path: '/my-rewards', view: 'activity' },
  { id: 'u-badges', path: '/badges', view: 'badges' },
  { id: 'u-campaigns', path: '/campaigns', view: 'campaigns' },
  { id: 'u-store', path: '/catalogue', view: 'store' },
  { id: 'u-leaderboard', path: '/leaderboard', view: 'leaderboard' },
  { id: 'u-howto', path: '/how-to-earn', view: 'howto' },
  { id: 'u-privacy', path: '/privacy', view: 'privacy' },
];

const byId = new Map(MEMBER_ROUTES.map((r) => [r.id, r]));

/** Canonical client path for a member tab. */
export function screenToRoute(id: MemberScreenId): string {
  return byId.get(id)?.path ?? '/';
}

/** The member tab a canonical client path maps to (falls back to the dashboard). */
export function routeToScreen(path: string): MemberScreenId {
  const clean = normalizePath(path).split('?')[0];
  return MEMBER_ROUTES.find((r) => r.path === clean)?.id ?? 'u-dashboard';
}

/**
 * The navigation target for a tab: the SEF href the server built (for the real
 * anchor) plus the router path to push and the canonical route to stash.
 */
export function navTarget(id: MemberScreenId): { href: string; target: string; route: string } {
  const route = screenToRoute(id);
  const view = byId.get(id)?.view ?? 'app';
  const link = joomlaOptions().navigation[view];
  const href = link?.url ?? route;
  return { href, target: link ? toRouterPath(link.url, route) : route, route };
}

export function routerBasePath(): string {
  return normalizeBasePath(joomlaOptions().router?.basePath ?? '');
}

/** The canonical route the server resolved for this request (refresh / deep link). */
export function serverResolvedRoute(): string {
  const o = joomlaOptions();
  return o.resolvedRoute?.path || o.initialRoute || '/';
}

/** The canonical route stashed on history state by a client-side navigation, if any. */
export function routeFromState(state: unknown): string {
  if (!state || typeof state !== 'object') return '';
  const route = (state as Record<string, unknown>)[ROUTE_STATE_KEY];
  return typeof route === 'string' ? route : '';
}

/** Map the current SEF location back to a known canonical route via the nav map. */
export function routeFromNavigation(pathname: string, search: string): string {
  const nav = joomlaOptions().navigation;
  for (const link of Object.values(nav)) {
    if (link?.route && isSameRoute(toRouterPath(link.url, link.route), pathname, search)) {
      return link.route;
    }
  }
  return '';
}

/**
 * Convert a Joomla SEF URL (or an already-canonical route) into a path the
 * router can navigate to, stripping the origin and the subfolder basename.
 */
export function toRouterPath(value: string | undefined | null, fallback = '/'): string {
  const raw = (value ?? '').trim();
  if (raw === '') return fallback;

  const url = safeUrl(raw);
  const basePath = routerBasePath();

  if (url && url.origin === window.location.origin) {
    return stripBasePath(`${url.pathname}${url.search}`, basePath);
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return raw;
  return stripBasePath(raw, basePath);
}

function isSameRoute(route: string, pathname: string, search: string): boolean {
  const [routePath, routeSearch = ''] = route.split('?', 2);
  const normalizedSearch = routeSearch ? `?${routeSearch}` : '';
  return normalizePath(routePath) === normalizePath(pathname) && normalizedSearch === (search || '');
}

function safeUrl(value: string): URL | null {
  try {
    return new URL(value, window.location.origin);
  } catch {
    return null;
  }
}

function stripBasePath(value: string, basePath: string): string {
  const normalized = normalizePath(value);
  if (!basePath || basePath === '/') return normalized;
  if (normalized === basePath) return '/';
  if (normalized.startsWith(`${basePath}/`)) return normalizePath(normalized.slice(basePath.length));
  return normalized;
}

function normalizeBasePath(value: string): string {
  const normalized = normalizePath(value);
  return normalized === '/' ? '' : normalized.replace(/\/+$/, '');
}

function normalizePath(value: string): string {
  const [path = '', query = ''] = value.split('?', 2);
  const normalizedPath = `/${path}`.replace(/\/+/g, '/');
  return `${normalizedPath === '/' ? '/' : normalizedPath.replace(/\/+$/, '')}${query ? `?${query}` : ''}`;
}
